import {
    createContext, useState, useEffect, useRef, useCallback,
} from 'react';
import {
    redisCollectionService,
    subOrganizationService
} from 'shared/api';
import {
    useParams
} from 'react-router-dom';
import subOrgSectionMenu from 'shared/data/subOrgSectionMenu';
import type {
    KBCollection,
    SubOrgAction,
    SubOrgContextValue,
    SubOrganizationProviderProps,
    SubOrganizationMinimal,
    VectorIndex
} from './context.types';

const defaultSubOrgContext: SubOrgContextValue = {
    actions: () => {
        // Default empty action handler
    },
    loading: true,
    subOrgMenu: [],
    collections: [],
    selectedSubOrganization: {},
    collectionLoading: false,
    collectionsError: null,
    vectorIndex: null,
    hasMilvusCollection: null,
    kbConfigLoading: true,
    fetchCollections: async () => {},
    refreshKbConfig: async () => {},
    isCollectionsLoaded: false
};

export const SubOrgContext = createContext<SubOrgContextValue>(defaultSubOrgContext);

export const SubOrganizationProvider = ({
    children
}: SubOrganizationProviderProps) => {
    const [selectedSubOrganization, setSelectedSubOrganization] = useState<SubOrganizationMinimal>({});
    const [loading, setLoading] = useState<boolean>(false);
    const [collections, setCollections] = useState<KBCollection[]>([]);
    const [hasMilvusCollection, setHasMilvusCollection] = useState<boolean | null>(null);
    const [vectorIndex, setVectorIndex] = useState<VectorIndex | null>(null);
    const [kbConfigLoading, setKbConfigLoading] = useState<boolean>(true);
    const [collectionLoading, setCollectionLoading] = useState<boolean>(false);
    const [collectionsError, setCollectionsError] = useState<string | null>(null);
    const [isCollectionsLoaded, setIsCollectionsLoaded] = useState<boolean>(false);
    const [subOrgMenu] = useState(subOrgSectionMenu as unknown as SubOrgContextValue['subOrgMenu']);

    // Track if collections have been fetched for the current sub-org session
    const collectionsFetched = useRef<boolean>(false);
    // Prevent duplicate concurrent fetches
    const isFetchingCollections = useRef<boolean>(false);
    // Guard against setting state after unmount
    const isMounted = useRef<boolean>(true);

    const {
        oragID, subOragID
    } = useParams();

    // Cleanup effect for mount/unmount
    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);

    // Fetch KB configuration data (milvus status, vector index) - NOT collections
    const fetchSubOrgData = useCallback(async () => {
        setKbConfigLoading(true);
        setHasMilvusCollection(null);
        setVectorIndex(null);
        try {
            const hasMilvusResponse = await subOrganizationService.subOrganizationKbData(oragID!, subOragID!);
            if (!isMounted.current) return;
            const { vectorIndex: indexData, milvus } = hasMilvusResponse.data;
            setVectorIndex(indexData ?? null);
            setHasMilvusCollection(milvus?.value ?? false);
        } catch (error) {
            console.error('[SubOrgContext] Error fetching sub-org KB config:', error);
            if (isMounted.current) {
                setVectorIndex(null);
                setHasMilvusCollection(false);
            }
        } finally {
            if (isMounted.current) {
                setKbConfigLoading(false);
            }
        }
    }, [oragID, subOragID]);

    useEffect(() => {
        collectionsFetched.current = false;
        isFetchingCollections.current = false;
        setIsCollectionsLoaded(false);
        setCollections([]);
        setCollectionsError(null);
        fetchSubOrgData();
    }, [subOragID, fetchSubOrgData]);
    const actions = (action: SubOrgAction) => {
        const {
            type, payload
        } = action;

        switch (type) {
            case 'SET_LOADING':
                return setLoading(payload);
            case 'SET_COLLECTIONS':
                return setCollections(payload);
            case 'SET_SELECTED_SUB_ORGANIZATION':
                return setSelectedSubOrganization(payload);
            case 'SET_VECTOR_INDEX':
                return setVectorIndex(payload);
            case 'SET_MILVUS_COLLECTION_STATUS':
                return setHasMilvusCollection(payload);
            default:
                return loading;
        }
    };

    // Lazy fetch collections - only fetches if not already fetched for this sub-org
    const fetchCollections = useCallback(async () => {
        if (collectionsFetched.current) {
            console.log('[SubOrgContext] Collections already fetched, skipping...');
            return;
        }

        if (isFetchingCollections.current) {
            console.log('[SubOrgContext] Collections fetch already in progress, skipping duplicate...');
            return;
        }

        console.log('[SubOrgContext] Fetching collections...');
        isFetchingCollections.current = true;
        setCollectionLoading(true);
        setCollectionsError(null);

        try {
            const projectsResponse = await redisCollectionService.listCollections(oragID, subOragID);
            if (!isMounted.current) return;
            setCollections(projectsResponse.data as KBCollection[]);
            collectionsFetched.current = true;
            setIsCollectionsLoaded(true);
            console.log('[SubOrgContext] Collections fetched successfully.');
        } catch (error) {
            console.error('[SubOrgContext] Error fetching collections:', error);
            if (isMounted.current) {
                setCollectionsError('Failed to load collections. Please try again.');
            }
        } finally {
            isFetchingCollections.current = false;
            if (isMounted.current) {
                setCollectionLoading(false);
            }
        }
    }, [oragID, subOragID]);

    return (
        <SubOrgContext.Provider
            value={{
                actions,
                loading,
                subOrgMenu,
                collections,
                selectedSubOrganization,
                collectionLoading,
                collectionsError,
                vectorIndex,
                hasMilvusCollection,
                kbConfigLoading,
                fetchCollections,
                refreshKbConfig: fetchSubOrgData,
                isCollectionsLoaded
            }}
        >
            {children}
        </SubOrgContext.Provider>
    );
};
